import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import api from "../../api/api";
import { Alert, Field, Panel, apiError, useOrgStyles, ORG_TYPE_LABEL } from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";

const CONTACT_KEYS = [
    "primaryContactName", "primaryContactEmail", "primaryContactPhone",
    "secondaryContactName", "secondaryContactEmail", "secondaryContactPhone",
];

/**
 * Org admin settings: primary and secondary points of contact. The organization
 * name is fixed once the org exists; only Build My Biz Brand can change it.
 */
export default function OrgSettings() {
    const { orgId, org, reloadOrg, isPlatformAdmin } = useOrgPortal();
    const s = useOrgStyles();
    const [form, setForm] = useState({
        primaryContactName: org.PrimaryContactName || "",
        primaryContactEmail: org.PrimaryContactEmail || "",
        primaryContactPhone: org.PrimaryContactPhone || "",
        secondaryContactName: org.SecondaryContactName || "",
        secondaryContactEmail: org.SecondaryContactEmail || "",
        secondaryContactPhone: org.SecondaryContactPhone || "",
    });
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState({ kind: "", text: "" });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/org/${orgId}`, Object.fromEntries(CONTACT_KEYS.map((k) => [k, form[k]])));
            await reloadOrg();
            setMsg({ kind: "success", text: "Saved." });
        } catch (err) {
            setMsg({ kind: "error", text: apiError(err) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
                <Panel title="Organization">
                    <Field label="Organization name" htmlFor="os-name">
                        <div className="relative">
                            <input id="os-name" className={`${s.input} pr-10 opacity-75`} value={org.OrgName || ""} readOnly aria-readonly="true" />
                            <Lock size={16} className={`absolute right-3 top-1/2 -translate-y-1/2 ${s.muted}`} aria-hidden="true" />
                        </div>
                    </Field>
                    <p className={`mt-2 text-xs ${s.muted}`}>
                        {isPlatformAdmin
                            ? <>Rename organizations in <Link className={s.btnLink} to="/admin/organizations">Admin → Organizations</Link>.</>
                            : "Need to change your organization's name? Contact Build My Biz Brand."}
                    </p>
                </Panel>
                <Panel title="Points of contact">
                    <Alert kind={msg.kind || "error"} onClose={() => setMsg({ kind: "", text: "" })}>{msg.text}</Alert>
                    <form onSubmit={save} className="space-y-6">
                        <ContactFields prefix="primary" title="Primary contact" form={form} set={set} />
                        <ContactFields prefix="secondary" title="Secondary contact (optional)" form={form} set={set} />
                        <div className="flex justify-end">
                            <button type="submit" className={s.btnPrimary} disabled={saving}>
                                {saving && <Loader2 size={14} className="animate-spin" />} Save changes
                            </button>
                        </div>
                    </form>
                </Panel>
            </div>
            <Panel title="Plan">
                <dl className={`space-y-3 text-sm ${s.body}`}>
                    <div className="flex justify-between"><dt>Type</dt><dd className={s.text}>{ORG_TYPE_LABEL[org.OrgType]}</dd></div>
                    {/* old <div className="flex justify-between"><dt>Coach licenses</dt><dd className={s.text}>{org.CoachLicenseLimit}</dd></div> */}
                    {/* "added" rather than "purchased": Build My Biz Brand can also set seats up for free */}
                    <div className="flex justify-between"><dt>Coach licenses</dt><dd className={s.text}>{org.CoachLicenseLimit}{org.CoachLicensesPurchased > 0 && ` + ${org.CoachLicensesPurchased} added`}</dd></div>
                    {/* old <div className="flex justify-between"><dt>Student licenses</dt><dd className={s.text}>{org.StudentLicenseLimit}</dd></div> */}
                    <div className="flex justify-between"><dt>Student licenses</dt><dd className={s.text}>{org.StudentLicenseLimit}{org.StudentLicensesPurchased > 0 && ` + ${org.StudentLicensesPurchased} added`}</dd></div>
                    <div className="flex justify-between"><dt>Programs</dt><dd className={s.text}>{org.ProgramLimit}</dd></div>
                    <div className="flex justify-between"><dt>Student plan</dt><dd className={s.text}>{org.SponsoredStudentTierName || "None"}</dd></div>
                </dl>
                <p className={`mt-4 text-xs ${s.muted}`}>
                    {isPlatformAdmin
                        ? <>Change limits and plans in <Link className={s.btnLink} to="/admin/organizations">Admin → Organizations</Link>.</>
                        : "To change your plan or limits, contact Build My Biz Brand."}
                </p>
            </Panel>
        </div>
    );
}

function ContactFields({ prefix, title, form, set }) {
    const s = useOrgStyles();
    return (
        <fieldset>
            <legend className={`mb-3 text-sm font-semibold ${s.text}`}>{title}</legend>
            <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name" htmlFor={`os-${prefix}-name`} className="sm:col-span-2">
                    <input id={`os-${prefix}-name`} className={s.input} value={form[`${prefix}ContactName`]} onChange={set(`${prefix}ContactName`)} maxLength={200} />
                </Field>
                <Field label="Email" htmlFor={`os-${prefix}-email`}>
                    <input id={`os-${prefix}-email`} type="email" className={s.input} value={form[`${prefix}ContactEmail`]} onChange={set(`${prefix}ContactEmail`)} maxLength={255} />
                </Field>
                <Field label="Phone" htmlFor={`os-${prefix}-phone`}>
                    <input id={`os-${prefix}-phone`} type="tel" className={s.input} value={form[`${prefix}ContactPhone`]} onChange={set(`${prefix}ContactPhone`)} maxLength={50} />
                </Field>
            </div>
        </fieldset>
    );
}
